import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { createStore } from 'zustand';
import { combine } from 'zustand/middleware';

import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import type { RoomEditedField } from 'app/typings/core/extranet/inventory/enums/RoomEditedField';
import type { InventoryResponse } from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import { add } from 'app/utils/dayjs';

import type { TimePeriod } from '../Legacy/type';

import {
  ExperienceInventoryType,
  PackType,
} from './_components/Header/ActionsBar/InventoryToggle/InventoryToggle';
import { retrieveFormValues } from './utils';
import { appendEditedField } from './utils/editedFields';
import type { EditedRoomFields } from './utils/editedFields';
import type { ComputePacksResult } from './utils/packs';

export const INVENTORY_DATE_SIZE = 15;
export const SALES_DATE_SIZE = 57;

export type InventoryDependencies = {
  saleEndDate: Date;
  packData: ExtranetHotelPack[];
  filteredExperiences: ExtranetHotelExperience[];
  inventoryInput: InventoryResponse;
  hasChannelManager: boolean;
  hasAutoInventory: boolean;
  defaultNightRoomDiscount?: number | null;
  lastMinuteNightRoomDiscountRate?: number | null;
  lastMinuteNightRoomDiscountDays?: number | null;
};

export enum InventoryTabItemName {
  Packs = 'Packages',
  Rooms = 'Rooms',
  Experiences = 'Activities & amenities',
}

export type InventoryTabItem = {
  name: InventoryTabItemName;
};

export const INVENTORY_TAB_ITEMS = [
  { name: InventoryTabItemName.Packs },
  { name: InventoryTabItemName.Rooms },
  { name: InventoryTabItemName.Experiences },
];

type ChangesModalData = {
  isVisible: boolean;
  tabSelect?: InventoryTabItemName;
};

export type InventoryItem = Record<string, unknown>;

export type ModifiedData = Record<
  string,
  Record<string | number, InventoryItem>
>;

export type InventoryExperiences = Record<
  string,
  Record<number, ComputePacksResult[string]['experiences'][number]>
>;

export type InventoryRooms = Record<
  string,
  Record<number, ComputePacksResult[string]['rooms'][number]>
>;

export type InventoryPacks = Record<
  string,
  Record<number, ComputePacksResult[string]['packs'][number]>
>;

type InventoryStoreValuesType = {
  inventory?: ComputePacksResult;
  rawInventory?: InventoryResponse;
  modifiedInventory?: ComputePacksResult;
  inventoryExperiences?: InventoryExperiences;
  inventoryRooms?: InventoryRooms;
  inventoryPacks?: InventoryPacks;
  form?: FormInstance<InventoryExperiences | InventoryRooms | InventoryPacks>;
  isEditMode: boolean;
  tab: InventoryTabItem;
  duplicationDateSelection: string[];
  timePeriod: TimePeriod;
  endOfSaleDate: string;
  timePeriodStore: Partial<ComputePacksResult>;
  packType: PackType;
  experienceInventoryType: ExperienceInventoryType;
  changesModalData: ChangesModalData;
  experienceBulkEditDrawer: { expId?: number; isOpened?: boolean };
  roomBulkEditDrawer: { roomId?: number; isOpened?: boolean };
  drawerPackConfig?: { packId?: number; isOpen?: boolean };
  experienceStockConfig: {
    expId?: number;
    isOpen: boolean;
  };
  timeslotsModal: {
    expId?: number;
    isOpen: boolean;
    selectedActivityId?: number;
  };
  isPricingDrawerOpened: boolean;
  openPricingFromTask: boolean;
  dependencies?: InventoryDependencies;
  packIdForPricingDrawer?: number;
  editedRoomFields: EditedRoomFields;
  hasDeactivatedPricingRuleByEdit: boolean;
};

type InventoryStoreMutationType = {
  setInventory: (inventory: ComputePacksResult | undefined) => void;
  setRawInventory: (inventory: InventoryResponse | undefined) => void;
  setModifiedInventory: (modifiedInventory: ComputePacksResult) => void;
  setForm: (
    form: FormInstance<InventoryExperiences | InventoryRooms | InventoryPacks>
  ) => void;
  setIsEditMode: (value: boolean) => void;
  changeTab: (tab: InventoryTabItem) => void;
  setDuplicationDateSelection: (dates: string[]) => void;
  setTimePeriod: (startDate: string | Dayjs, endDate: string | Dayjs) => void;
  hasPeriodData: (startDate: string | Dayjs) => boolean;
  getAllModifiedPeriods: () => Partial<ComputePacksResult>;
  clearAllPeriods: () => void;
  changeTimePeriod: (newStartDate: string | Dayjs) => void;
  setPackType: (value: PackType) => void;
  setExperienceInventoryType: (value: ExperienceInventoryType) => void;
  setChangesModalData: ({ isVisible, tabSelect }: ChangesModalData) => void;
  setExperienceBulkEditDrawer: (expId?: number, isOpen?: boolean) => void;
  setRoomBulkEditDrawer: (expId?: number, isOpen?: boolean) => void;
  setDrawerPackConfig: (packId?: number, isOpen?: boolean) => void;
  setPricingDrawerOpen: (isOpen?: boolean) => void;
  setOpenPricingFromTask: (value: boolean) => void;
  setDependencies: (dependencies: InventoryDependencies) => void;
  setTimePeriodStore: (timePeriodStore: Partial<ComputePacksResult>) => void;
  setExperienceStockConfig: (expId?: number, isOpen?: boolean) => void;
  setTimeslotsModal: (expId?: number, isOpen?: boolean) => void;
  setTimeslotsSelectedActivityId: (activityId?: number) => void;
  setPackIdForPricingDrawer: (packId?: number) => void;
  addEditedRoomField: (
    dateKey: string,
    roomId: number,
    field: RoomEditedField
  ) => void;
  clearEditedRoomFields: () => void;
  setHasDeactivatedPricingRuleByEdit: (value: boolean) => void;
};

const normalizeDateKey = (date: string | Dayjs): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

const initialValues: InventoryStoreValuesType = {
  inventory: undefined,
  inventoryExperiences: undefined,
  inventoryRooms: undefined,
  inventoryPacks: undefined,
  form: undefined,
  isEditMode: false,
  tab: INVENTORY_TAB_ITEMS[0],
  duplicationDateSelection: [],
  timePeriod: {
    startDate: new Date(),
    endDate: dayjs()
      .add(INVENTORY_DATE_SIZE - 1, 'day')
      .toDate(),
  },
  endOfSaleDate: formatDateWithoutHour(
    add(new Date(), { days: SALES_DATE_SIZE })
  ),
  timePeriodStore: {},
  packType: PackType.NIGHT_STAY,
  experienceInventoryType: ExperienceInventoryType.DAILY_STOCK,
  changesModalData: { isVisible: false },
  experienceBulkEditDrawer: { expId: undefined, isOpened: false },
  roomBulkEditDrawer: { roomId: undefined, isOpened: false },
  drawerPackConfig: { packId: undefined, isOpen: false },
  isPricingDrawerOpened: false,
  openPricingFromTask: false,
  experienceStockConfig: {
    isOpen: false,
  },
  timeslotsModal: {
    isOpen: false,
  },
  dependencies: undefined,
  packIdForPricingDrawer: undefined,
  editedRoomFields: {},
  hasDeactivatedPricingRuleByEdit: false,
};

export const inventoryStore = createStore(
  /**
   * Uncomment the following line to enable debugging with Redux DevTools
   * https://github.com/pmndrs/zustand?tab=readme-ov-file#redux-devtools
   * https://chromewebstore.google.com/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd?hl=en
   */
  // devtools(
  combine<InventoryStoreValuesType, InventoryStoreMutationType>(
    initialValues,
    (set, get) => ({
      setInventory: (inventory: ComputePacksResult | undefined) => {
        if (!inventory) {
          set({
            inventory: undefined,
            inventoryExperiences: undefined,
            inventoryRooms: undefined,
            inventoryPacks: undefined,
          });

          return;
        }

        const { inventoryExperiences, inventoryRooms, inventoryPacks } =
          retrieveFormValues(inventory);

        set({
          inventory,
          inventoryExperiences,
          inventoryRooms,
          inventoryPacks,
          modifiedInventory: inventory,
        });
      },
      setModifiedInventory: (modifiedInventory: ComputePacksResult) => {
        set({ modifiedInventory });
      },
      setRawInventory: (inventory: InventoryResponse | undefined) => {
        set({ rawInventory: inventory });
      },
      setForm: (
        form: FormInstance<
          InventoryExperiences | InventoryRooms | InventoryPacks
        >
      ) => set({ form }),
      setIsEditMode: (isEditMode: boolean) => set({ isEditMode }),
      changeTab: (tab: InventoryTabItem) =>
        set((state) => {
          const isSameTab = state.tab.name === tab.name;

          return {
            tab,
            packType: isSameTab ? state.packType : PackType.NIGHT_STAY,
            experienceInventoryType: isSameTab
              ? state.experienceInventoryType
              : ExperienceInventoryType.DAILY_STOCK,
          };
        }),
      setDuplicationDateSelection: (dates: string[]) =>
        set({ duplicationDateSelection: dates }),
      setTimePeriod: (startDate, endDate) => {
        const start = normalizeDateKey(startDate);
        const end = normalizeDateKey(endDate);

        set({
          timePeriod: {
            startDate: dayjs(start).toDate(),
            endDate: dayjs(end).toDate(),
          },
        });
      },

      hasPeriodData: (startDate: string | Dayjs) => {
        const { timePeriodStore } = get();

        const key = normalizeDateKey(startDate);

        return !!timePeriodStore[key];
      },

      getAllModifiedPeriods: () => {
        const { timePeriodStore } = get();

        return { ...timePeriodStore };
      },

      clearAllPeriods: () => {
        set({
          timePeriodStore: {},
          isEditMode: false,
          editedRoomFields: {},
          hasDeactivatedPricingRuleByEdit: false,
        });
      },

      setPackType: (value: PackType) => set({ packType: value }),
      setExperienceInventoryType: (value: ExperienceInventoryType) =>
        set({ experienceInventoryType: value }),

      setChangesModalData: ({ isVisible, tabSelect }: ChangesModalData) =>
        set({ changesModalData: { isVisible, tabSelect } }),
      setExperienceBulkEditDrawer: (expId?: number, isOpened?: boolean) =>
        set({ experienceBulkEditDrawer: { expId, isOpened } }),
      setRoomBulkEditDrawer: (roomId?: number, isOpened?: boolean) =>
        set({ roomBulkEditDrawer: { roomId, isOpened } }),
      changeTimePeriod: (newStartDate: string | Dayjs) => {
        const newKey = normalizeDateKey(newStartDate);

        set({
          timePeriod: {
            startDate: dayjs(newKey).toDate(),
            endDate: dayjs(newKey)
              .add(INVENTORY_DATE_SIZE - 1, 'day')
              .toDate(),
          },
        });
      },
      setDrawerPackConfig: (packId?: number, isOpen = false) =>
        set({ drawerPackConfig: { packId, isOpen } }),
      setPricingDrawerOpen: (isOpen = false) =>
        set({ isPricingDrawerOpened: isOpen }),
      setOpenPricingFromTask: (value: boolean) =>
        set({ openPricingFromTask: value }),
      setDependencies: (dependencies: InventoryDependencies) =>
        set({ dependencies }),
      setTimePeriodStore: (timePeriodStore: Partial<ComputePacksResult>) =>
        set({ timePeriodStore }),

      setExperienceStockConfig: (expId?: number, isOpen = false) =>
        set({ experienceStockConfig: { expId, isOpen } }),
      setTimeslotsModal: (expId?: number, isOpen = false) =>
        set({ timeslotsModal: { expId, isOpen } }),
      setTimeslotsSelectedActivityId: (activityId?: number) =>
        set((state) => ({
          timeslotsModal: {
            ...state.timeslotsModal,
            selectedActivityId: activityId,
          },
        })),
      setPackIdForPricingDrawer: (packId?: number) =>
        set({ packIdForPricingDrawer: packId }),
      addEditedRoomField: (
        dateKey: string,
        roomId: number,
        field: RoomEditedField
      ) => {
        const { editedRoomFields } = get();
        const forDate = editedRoomFields[dateKey] ?? {};
        const forRoom = forDate[roomId] ?? [];

        set({
          editedRoomFields: {
            ...editedRoomFields,
            [dateKey]: {
              ...forDate,
              [roomId]: appendEditedField(forRoom, field),
            },
          },
        });
      },
      clearEditedRoomFields: () => set({ editedRoomFields: {} }),
      setHasDeactivatedPricingRuleByEdit: (value: boolean) =>
        set({ hasDeactivatedPricingRuleByEdit: value }),
    })
  )
  // )
);
